/**
 * CITY 인벤토리 + 도시 설정 목록 (getCityInventory, getCityConfig)
 * 💰 [비용 최적화] localStorage 5분 캐시 추가 - 동일 userId/env 재호출 시 서버 요청 스킵
 * 💰 [비용 최적화] getCityConfig도 5분 캐시 적용 - env당 1회 서버 요청으로 제한
 */
import { useState, useEffect, useCallback } from 'react';
import { getCityInventory, getCityConfig, getCityMintedCounts } from '../../../services/cityApi';
import { DEFAULT_CITY_CONFIG_LIST } from '../constants';

const EMPTY_EQUIPPED_SLOTS = [null, null, null];

// 인벤토리 캐시 TTL: 5분
const INVENTORY_CACHE_TTL = 5 * 60 * 1000;

// getCityConfig 캐시 TTL: 5분 (env 단위, userId 불필요)
const CONFIG_CACHE_TTL = 5 * 60 * 1000;

const readConfigCache = (environment) => {
  try {
    const raw = localStorage.getItem(`city_config_${environment}`);
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp < CONFIG_CACHE_TTL) return data;
    localStorage.removeItem(`city_config_${environment}`);
  } catch (e) { /* ignore */ }
  return null;
};

const writeConfigCache = (environment, data) => {
  try {
    localStorage.setItem(`city_config_${environment}`, JSON.stringify({ data, timestamp: Date.now() }));
  } catch (e) { /* ignore */ }
};

const clearConfigCache = (environment) => {
  try {
    localStorage.removeItem(`city_config_${environment}`);
  } catch (e) { /* ignore */ }
};

const getInventoryCacheKey = (userId, environment) =>
  `city_inventory_${userId}_${environment}`;

const readInventoryCache = (userId, environment) => {
  try {
    const key = getInventoryCacheKey(userId, environment);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp < INVENTORY_CACHE_TTL) return data;
    localStorage.removeItem(key); // 만료된 캐시 제거
  } catch (e) { /* ignore */ }
  return null;
};

const writeInventoryCache = (userId, environment, data) => {
  try {
    const key = getInventoryCacheKey(userId, environment);
    localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
  } catch (e) { /* ignore */ }
};

const clearInventoryCache = (userId, environment) => {
  try {
    localStorage.removeItem(getInventoryCacheKey(userId, environment));
  } catch (e) { /* ignore */ }
};

// 🔧 [Cost Optimization 2026-03-03] getCityMintedCounts 캐시 (5분 TTL)
const MINTED_CACHE_TTL = 5 * 60 * 1000;

const readMintedCache = (environment) => {
  try {
    const raw = localStorage.getItem(`city_minted_${environment}`);
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp < MINTED_CACHE_TTL) return data;
    localStorage.removeItem(`city_minted_${environment}`);
  } catch (e) { /* ignore */ }
  return null;
};

const writeMintedCache = (environment, data) => {
  try {
    localStorage.setItem(`city_minted_${environment}`, JSON.stringify({ data, timestamp: Date.now() }));
  } catch (e) { /* ignore */ }
};

const clearMintedCache = (environment) => {
  try {
    localStorage.removeItem(`city_minted_${environment}`);
  } catch (e) { /* ignore */ }
};

/**
 * @param {string} [userId]
 * @param {string} [environment]
 * @returns {object} configList, owned, equippedList, equippedTokenIds, cityUnlockedSlots, equipped, equippedTokenId, …
 */
export function useCityInventory(userId, environment) {
  const [configList, setConfigList] = useState(DEFAULT_CITY_CONFIG_LIST);
  const [owned, setOwned] = useState([]);
  const [equipped, setEquipped] = useState(null);
  const [equippedTokenId, setEquippedTokenId] = useState(null);
  const [equippedList, setEquippedList] = useState(EMPTY_EQUIPPED_SLOTS);
  const [equippedTokenIds, setEquippedTokenIds] = useState(EMPTY_EQUIPPED_SLOTS);
  const [cityUnlockedSlots, setCityUnlockedSlots] = useState(1);
  // 🎯 [다중 구매] 도시별 보유 개수 — { [cityId]: count }
  const [ownedCounts, setOwnedCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchConfig = useCallback(async (forceRefresh = false) => {
    // 캐시 히트 시 서버 요청 스킵 (env 단위 5분 TTL)
    if (!forceRefresh) {
      const cached = readConfigCache(environment);
      if (cached) {
        setConfigList(cached.length > 0 ? cached : DEFAULT_CITY_CONFIG_LIST);
        return;
      }
    }
    try {
      const { list } = await getCityConfig(environment);
      const raw = Array.isArray(list) ? list : [];
      // getCityConfig(includeMintedCount:true)가 실패·정적 목록만 온 경우에만 캐시 문서로 보조
      const needsMintedFallback = raw.some((item) => !Number.isFinite(item.mintedCount));
      let counts = {};
      if (needsMintedFallback) {
        const cached = readMintedCache(environment);
        if (cached) {
          counts = cached;
        } else {
          const r = await getCityMintedCounts(environment);
          counts = r.counts || {};
          writeMintedCache(environment, counts);
        }
      }

      // 🎯 [Safety Fix] 백엔드 데이터에 가격 정보가 없으면 기본값 병합
      // mintedCount: 서버 count()(정본) 우선 → 없을 때만 cache/city_minted_counts
      const safeList = raw.map((item) => {
        const defaultItem = DEFAULT_CITY_CONFIG_LIST.find((d) => d.id === item.id);
        const fromCount = Number.isFinite(item.mintedCount) ? item.mintedCount : null;
        const fromCacheDoc = counts[item.id];
        const mintedCount =
          fromCount !== null
            ? fromCount
            : Number.isFinite(fromCacheDoc)
              ? fromCacheDoc
              : item.mintedCount ?? 0;

        return {
          ...item,
          priceSpot: item.priceSpot ?? defaultItem?.priceSpot,
          priceStar: item.priceStar ?? defaultItem?.priceStar,
          maxSupply: item.maxSupply ?? defaultItem?.maxSupply ?? 1000,
          mintedCount
        };
      });

      const finalList = safeList.length > 0 ? safeList : DEFAULT_CITY_CONFIG_LIST;
      setConfigList(finalList);
      writeConfigCache(environment, finalList);
    } catch (e) {
      setConfigList(DEFAULT_CITY_CONFIG_LIST);
    }
  }, [environment]);

  const fetchInventory = useCallback(async (forceRefresh = false) => {
    if (!userId) {
      setOwned([]);
      setEquipped(null);
      setEquippedTokenId(null);
      setEquippedList([...EMPTY_EQUIPPED_SLOTS]);
      setEquippedTokenIds([...EMPTY_EQUIPPED_SLOTS]);
      setCityUnlockedSlots(1);
      setOwnedCounts({});
      setLoading(false);
      return;
    }

    // 📦 캐시 확인 (강제 갱신 요청이 아닐 때만)
    if (!forceRefresh) {
      const cached = readInventoryCache(userId, environment);
      if (cached) {
        setOwned(cached.owned || []);
        setEquipped(cached.equipped ?? null);
        setEquippedTokenId(cached.equippedTokenId ?? null);
        const el = cached.equippedList;
        const et = cached.equippedTokenIds;
        if (Array.isArray(el) && el.length === 3) {
          setEquippedList(el);
        } else {
          const next = [...EMPTY_EQUIPPED_SLOTS];
          if (cached.equipped) next[0] = cached.equipped;
          setEquippedList(next);
        }
        if (Array.isArray(et) && et.length === 3) {
          setEquippedTokenIds(et);
        } else {
          const next = [...EMPTY_EQUIPPED_SLOTS];
          if (cached.equippedTokenId) next[0] = cached.equippedTokenId;
          setEquippedTokenIds(next);
        }
        setCityUnlockedSlots(
          typeof cached.cityUnlockedSlots === 'number' && cached.cityUnlockedSlots >= 1
            ? Math.min(3, cached.cityUnlockedSlots)
            : 1
        );
        setOwnedCounts(cached.ownedCounts && typeof cached.ownedCounts === 'object' ? cached.ownedCounts : {});
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      const data = await getCityInventory(userId, environment);
      setOwned(data.owned || []);
      setEquipped(data.equipped ?? null);
      setEquippedTokenId(data.equippedTokenId ?? null);
      setEquippedList(Array.isArray(data.equippedList) ? data.equippedList : [...EMPTY_EQUIPPED_SLOTS]);
      setEquippedTokenIds(Array.isArray(data.equippedTokenIds) ? data.equippedTokenIds : [...EMPTY_EQUIPPED_SLOTS]);
      setCityUnlockedSlots(data.cityUnlockedSlots ?? 1);
      setOwnedCounts(data.ownedCounts && typeof data.ownedCounts === 'object' ? data.ownedCounts : {});
      writeInventoryCache(userId, environment, {
        owned: data.owned || [],
        equipped: data.equipped ?? null,
        equippedTokenId: data.equippedTokenId ?? null,
        equippedList: data.equippedList,
        equippedTokenIds: data.equippedTokenIds,
        cityUnlockedSlots: data.cityUnlockedSlots,
        ownedCounts: data.ownedCounts
      });
    } catch (e) {
      setError(e.message || 'Failed to load');
      setOwned([]);
      setEquipped(null);
      setEquippedTokenId(null);
      setEquippedList([...EMPTY_EQUIPPED_SLOTS]);
      setEquippedTokenIds([...EMPTY_EQUIPPED_SLOTS]);
      setCityUnlockedSlots(1);
      setOwnedCounts({});
    } finally {
      setLoading(false);
    }
  }, [userId, environment]);

  // refetch 호출 시 캐시를 무효화하고 강제 갱신 (구매·장착 직후 사용)
  const refetch = useCallback(() => {
    clearInventoryCache(userId, environment);
    clearMintedCache(environment); // 🔧 [Cost Optimization] minted count 캐시도 클리어
    clearConfigCache(environment); // 🔧 [Cost Optimization] config 캐시도 클리어
    fetchConfig(true);
    fetchInventory(true);
  }, [userId, environment, fetchConfig, fetchInventory]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  return {
    configList,
    owned,
    equipped,
    equippedTokenId,
    equippedList,
    equippedTokenIds,
    cityUnlockedSlots,
    ownedCounts,
    loading,
    error,
    refetch
  };
}
